import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { ScrollArea } from "@/components/ui/scroll-area";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Heart, Clock, Calendar as CalendarIcon, Users, Award, Trophy,
  Target, TrendingUp, Star, CheckCircle, MapPin, AlertCircle,
  ChevronRight, Gift, Shield, Home, Medal, Crown, Flame
} from "lucide-react";

const SHIFT_TYPE_COLORS = {
  companionship: "bg-blue-100 text-blue-700",
  respite: "bg-purple-100 text-purple-700",
  bereavement: "bg-indigo-100 text-indigo-700",
  administrative: "bg-gray-100 text-gray-700",
  special_event: "bg-pink-100 text-pink-700"
};

const BADGE_ICONS = {
  clock: Clock,
  award: Award,
  trophy: Trophy,
  crown: Crown,
  star: Star,
  heart: Heart,
  medal: Medal,
  users: Users,
  home: Home
};

export default function VolunteerPortal() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [impactData, setImpactData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showSignupDialog, setShowSignupDialog] = useState(false);
  const [signingUp, setSigningUp] = useState(false);

  useEffect(() => {
    // Use a default volunteer ID for demo/testing when no user is logged in
    const volunteerId = user?.id || "demo_volunteer";
    loadVolunteerData(volunteerId);
  }, [user]);

  const loadVolunteerData = async (volunteerId) => {
    setLoading(true);
    try {
      const [profileRes, shiftsRes, impactRes] = await Promise.all([
        api.get(`/phase24/volunteer/profile/${volunteerId}`).catch(() => ({ data: null })),
        api.get(`/phase24/volunteer/shifts/available/${volunteerId}`),
        api.get(`/phase24/volunteer/impact/${volunteerId}`)
      ]);
      
      setProfile(profileRes.data);
      setAvailableShifts(shiftsRes.data.shifts || []);
      setImpactData(impactRes.data);
    } catch (error) {
      console.error("Failed to load volunteer data:", error);
      toast.error("Failed to load volunteer data");
    } finally {
      setLoading(false);
    }
  };

  const handleSignupForShift = async () => {
    if (!selectedShift) return;
    
    setSigningUp(true);
    try {
      await api.post(`/phase24/volunteer/shifts/${selectedShift.id}/signup?volunteer_id=${user.id}`);
      toast.success("Successfully signed up for shift!");
      setShowSignupDialog(false);
      setSelectedShift(null);
      loadVolunteerData();
    } catch (error) {
      console.error("Failed to sign up:", error);
      toast.error(error.response?.data?.detail || "Failed to sign up for shift");
    } finally {
      setSigningUp(false);
    }
  };

  const handleCancelShift = async (shiftId) => {
    try {
      await api.delete(`/phase24/volunteer/shifts/${shiftId}/signup?volunteer_id=${user.id}`);
      toast.success("Shift cancelled successfully");
      loadVolunteerData();
    } catch (error) {
      console.error("Failed to cancel:", error);
      toast.error(error.response?.data?.detail || "Failed to cancel shift");
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "TBD";
    try {
      return new Date(dateStr).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit"
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="volunteer-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const stats = profile?.stats || impactData?.stats || {};

  return (
    <div className="min-h-screen bg-gradient-to-b from-rose-50 to-white" data-testid="volunteer-portal">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-pink-600 text-white py-8 px-4">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold">Welcome, {profile?.name || user?.name}!</h1>
              <p className="text-rose-100">Thank you for making a difference</p>
            </div>
            <div className="flex gap-4">
              <div className="text-center px-4 py-2 bg-white/10 rounded-lg">
                <p className="text-2xl font-bold">{stats.total_hours || 0}</p>
                <p className="text-xs text-rose-100">Total Hours</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/10 rounded-lg">
                <p className="text-2xl font-bold">{stats.patients_served || 0}</p>
                <p className="text-xs text-rose-100">Patients Served</p>
              </div>
              <div className="text-center px-4 py-2 bg-white/10 rounded-lg flex items-center gap-2">
                <Flame className="w-5 h-5" />
                <div>
                  <p className="text-2xl font-bold">{stats.current_streak_weeks || 0}</p>
                  <p className="text-xs text-rose-100">Week Streak</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="shifts" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-grid">
            <TabsTrigger value="shifts" data-testid="shifts-tab">
              <CalendarIcon className="w-4 h-4 mr-2" />Available Shifts
            </TabsTrigger>
            <TabsTrigger value="my-shifts" data-testid="my-shifts-tab">
              <CheckCircle className="w-4 h-4 mr-2" />My Schedule
            </TabsTrigger>
            <TabsTrigger value="impact" data-testid="impact-tab">
              <TrendingUp className="w-4 h-4 mr-2" />My Impact
            </TabsTrigger>
            <TabsTrigger value="badges" data-testid="badges-tab">
              <Award className="w-4 h-4 mr-2" />Badges
            </TabsTrigger>
          </TabsList>

          {/* Available Shifts Tab */}
          <TabsContent value="shifts">
            <Card>
              <CardHeader>
                <CardTitle>Available Volunteer Shifts</CardTitle>
                <CardDescription>Sign up for shifts that match your schedule and skills</CardDescription>
              </CardHeader>
              <CardContent>
                {availableShifts.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No available shifts at this time</p>
                    <p className="text-sm text-muted-foreground">Check back soon for new opportunities</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {availableShifts.map((shift) => (
                      <div
                        key={shift.id}
                        className={`p-4 rounded-lg border ${
                          shift.eligible?.eligible
                            ? "bg-white hover:shadow-md cursor-pointer"
                            : "bg-gray-50 opacity-75"
                        } transition-all`}
                        onClick={() => {
                          if (shift.eligible?.eligible) {
                            setSelectedShift(shift);
                            setShowSignupDialog(true);
                          }
                        }}
                        data-testid={`shift-${shift.id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className={SHIFT_TYPE_COLORS[shift.shift_type] || "bg-slate-100"}>
                                {shift.shift_type?.replace("_", " ")}
                              </Badge>
                              <span className="text-sm text-muted-foreground">
                                {shift.duration_hours} hours
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="flex items-center gap-1">
                                <CalendarIcon className="w-4 h-4" />
                                {formatDateTime(shift.start_time)}
                              </span>
                              {shift.patient_info && (
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {shift.patient_info.location_area}
                                </span>
                              )}
                            </div>
                            {shift.notes && (
                              <p className="text-sm text-muted-foreground mt-2">{shift.notes}</p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2">
                            {!shift.eligible?.eligible && (
                              <div className="text-sm text-orange-600">
                                <AlertCircle className="w-4 h-4 inline mr-1" />
                                {shift.eligible?.reasons?.[0] || "Not eligible"}
                              </div>
                            )}
                            {shift.eligible?.eligible && (
                              <Button size="sm">
                                Sign Up <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* My Schedule Tab */}
          <TabsContent value="my-shifts">
            <Card>
              <CardHeader>
                <CardTitle>My Upcoming Shifts</CardTitle>
                <CardDescription>Your scheduled volunteer commitments</CardDescription>
              </CardHeader>
              <CardContent>
                {profile?.upcoming_shifts?.length === 0 ? (
                  <div className="text-center py-12">
                    <CheckCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No upcoming shifts</p>
                    <Button className="mt-4" onClick={() => document.querySelector('[data-testid="shifts-tab"]')?.click()}>
                      Browse Available Shifts
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {profile?.upcoming_shifts?.map((shift) => (
                      <div
                        key={shift.id}
                        className="p-4 rounded-lg border bg-green-50 border-green-200"
                      >
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div>
                            <Badge className={SHIFT_TYPE_COLORS[shift.shift_type] || "bg-slate-100"}>
                              {shift.shift_type?.replace("_", " ")}
                            </Badge>
                            <div className="mt-2 space-y-1">
                              <p className="font-medium">
                                <CalendarIcon className="w-4 h-4 inline mr-1" />
                                {formatDateTime(shift.start_time)} - {formatDateTime(shift.end_time)}
                              </p>
                              {shift.patient_info && (
                                <>
                                  <p className="text-sm">
                                    <Users className="w-4 h-4 inline mr-1" />
                                    {shift.patient_info.first_name}
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    <MapPin className="w-4 h-4 inline mr-1" />
                                    {shift.patient_info.address}
                                  </p>
                                </>
                              )}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleCancelShift(shift.id)}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Impact Tab */}
          <TabsContent value="impact">
            <div className="space-y-6">
              {/* Impact Score */}
              <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                <CardContent className="py-6">
                  <div className="flex items-center gap-6">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center text-white">
                      <div className="text-center">
                        <p className="text-3xl font-bold">{stats.impact_score || 0}</p>
                        <p className="text-xs">IMPACT</p>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-amber-900">Your Impact Score</h3>
                      <p className="text-amber-700">
                        You've touched {stats.patients_served || 0} lives with {stats.total_hours || 0} hours of service
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Clock className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.total_hours || 0}</p>
                    <p className="text-sm text-muted-foreground">Total Hours</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.total_shifts || 0}</p>
                    <p className="text-sm text-muted-foreground">Shifts Completed</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <Heart className="w-8 h-8 mx-auto text-rose-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.patients_served || 0}</p>
                    <p className="text-sm text-muted-foreground">Patients Served</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6 text-center">
                    <CalendarIcon className="w-8 h-8 mx-auto text-purple-500 mb-2" />
                    <p className="text-2xl font-bold">{stats.monthly_shifts || 0}</p>
                    <p className="text-sm text-muted-foreground">This Month</p>
                  </CardContent>
                </Card>
              </div>

              {/* Milestones Progress */}
              {impactData?.milestones?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5" /> Next Milestones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {impactData.milestones.map((milestone) => (
                        <div key={milestone.type} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span>{milestone.description}</span>
                            <Badge variant="outline">
                              {milestone.current}/{milestone.target}
                            </Badge>
                          </div>
                          <Progress value={milestone.progress} className="h-2" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Monthly Breakdown */}
              {impactData?.monthly_breakdown?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>Monthly Activity</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {impactData.monthly_breakdown.slice(0, 6).map((month) => (
                        <div key={month.month} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                          <span className="font-medium">{month.month}</span>
                          <div className="flex items-center gap-4 text-sm">
                            <span>{month.shifts} shifts</span>
                            <span className="font-bold">{month.hours} hours</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Badges Tab */}
          <TabsContent value="badges">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5" /> Your Badges
                </CardTitle>
                <CardDescription>Recognition for your volunteer achievements</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {impactData?.badges?.map((badge) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Award;
                    return (
                      <div
                        key={badge.id}
                        className={`p-4 rounded-lg border-2 text-center ${
                          badge.earned
                            ? "bg-amber-50 border-amber-300"
                            : "bg-gray-50 border-dashed border-gray-200 opacity-50"
                        }`}
                      >
                        <div className={`w-12 h-12 mx-auto mb-2 rounded-full flex items-center justify-center ${
                          badge.earned ? "bg-amber-100" : "bg-gray-100"
                        }`}>
                          <IconComponent className={`w-6 h-6 ${badge.earned ? "text-amber-600" : "text-gray-400"}`} />
                        </div>
                        <p className="font-semibold text-sm">{badge.name}</p>
                        {badge.earned && (
                          <Badge className="mt-2 bg-amber-100 text-amber-800">
                            <CheckCircle className="w-3 h-3 mr-1" />Earned
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Shift Signup Dialog */}
      <Dialog open={showSignupDialog} onOpenChange={setShowSignupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Sign Up for Shift</DialogTitle>
            <DialogDescription>
              Confirm your commitment to this volunteer shift
            </DialogDescription>
          </DialogHeader>
          
          {selectedShift && (
            <div className="space-y-4 py-4">
              <div className="p-4 rounded-lg bg-slate-50">
                <Badge className={SHIFT_TYPE_COLORS[selectedShift.shift_type] || "bg-slate-100"}>
                  {selectedShift.shift_type?.replace("_", " ")}
                </Badge>
                <div className="mt-3 space-y-2">
                  <p className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {formatDateTime(selectedShift.start_time)}
                  </p>
                  <p className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {selectedShift.duration_hours} hours
                  </p>
                  {selectedShift.patient_info && (
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {selectedShift.patient_info.location_area}
                    </p>
                  )}
                </div>
                {selectedShift.notes && (
                  <p className="mt-3 text-sm text-muted-foreground">{selectedShift.notes}</p>
                )}
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  By signing up, you commit to attending this shift. Please cancel at least 24 hours in advance if you cannot attend.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignupDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSignupForShift} disabled={signingUp}>
              {signingUp ? "Signing Up..." : "Confirm Sign Up"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
